<template>
  <div :class="$style.projectSettingsEditContainer">
    <p class="margin-bottom24 text-input site-settings-info-copy">
      Sites that are added to a Data Collection Period cannot be changed or
      removed. Any changes here will be applied on the next data collection
      period.
    </p>
    <div class="margin-bottom80">
      <div
        v-for="(value, index) in localSelectedSiteArray"
        class="margin-bottom20"
        :key="index"
      >
        <div class="">
          <div class="level margin-bottom8">
            <div class="level-left">
              <label>{{ `Site ${index + 1}` }} </label>
            </div>
            <div class="level-right" :class="$style.marginRight50">
              <b-switch
                v-model="value.isIdentifierRequired"
                :disabled="!editable"
              >
                Required
              </b-switch>
              <b-tooltip
                v-if="value.isIdentifierRequired"
                label="Data collection sample code in individual mode is required, please confirm with project administrator."
                position="is-top"
                type="is-dark"
                size="is-large"
                multilined
              >
                <b-icon
                  icon="information-outline"
                  size="is-small"
                  custom-size="mdi-16px"
                  type="is-danger"
                >
                </b-icon>
              </b-tooltip>
              <b-tooltip
                v-else
                label="Data collection sample code in individual mode."
                position="is-top"
                type="is-dark"
              >
                <b-icon
                  icon="information-outline"
                  size="is-small"
                  custom-size="mdi-16px"
                  class="text-color"
                >
                </b-icon>
              </b-tooltip>
            </div>
          </div>
          <SiteAutoCompleteInput
            :editable="editable"
            v-if="sites && renderComponent"
            :sites="sites"
            :selectedSiteIndex="index"
            :localSelectedSiteArray="localSelectedSiteArray"
            @updateSelectedSiteArray="handleSelectedSiteArray"
            @removeSelectedSite="handleRemoveSelectedSite"
          />
        </div>
      </div>
      <div :class="$style.addItem" v-if="editable">
        <a @click="addSiteCard" class="add-site-text-field">+ Add Site</a>
      </div>
    </div>
    <div class="columns">
      <div class="column is-two-thirds vertical-center text-color"></div>
      <div class="column is-one-third">
        <b-button
          v-if="editable"
          :disabled="isSiteSubmitButtonDisabled"
          @click="updateSitesHandler"
          :class="['margin-right15', $style.saveChangesBtn]"
          type="is-primary"
          >Save</b-button
        >
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import SiteAutoCompleteInput from '@/views/Project/components/SiteAutoCompleteInput.vue';
import {
  ProjectDetailPayload,
  Site,
  ProjectSite,
  UpdateProjectSitesRequestPayload,
} from '../../../store/modules/projects/types/projects.types';
import { Action, State } from 'vuex-class';
import { ApiState } from '../../../store/types/general.types';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';
import {
  ToastProgrammatic as Toast,
  DialogProgrammatic as Dialog,
} from 'buefy';
import { RootState } from '@/store/store';

@Component({
  components: {
    SiteAutoCompleteInput,
  },
})
export default class SiteSettings extends Vue {
  @Prop({ default: true }) public editable!: boolean;
  @Prop() public projectDetail!: ProjectDetailPayload;

  @Action('projects/getSites')
  public getSites!: () => void;

  @State((state: RootState) => state.projects.sites)
  public sites!: Site[];

  @Action('projects/getProjectSites')
  public getProjectSites!: (projectId: number) => void;

  @State((state: RootState) => state.projects.apiState.getProjectSites)
  public getProjectSitesState!: ApiState;

  @State((state: RootState) => state.projects.projectSites)
  public projectSites!: ProjectSite[];

  @Action('projects/updateProjectSites')
  public updateProjectSites!: (
    params: UpdateProjectSitesRequestPayload,
  ) => void;

  @State((state) => state.projects.apiState.updateProjectSites)
  public getUpdateProjectSitesState!: ApiState;

  @State((state) => state.projects.updatedProjectSites)
  public updatedProjectSites!: any[];

  public localSelectedSiteArray: Site[] = [];
  public renderComponent: boolean = true;
  public isSiteSubmitButtonDisabled: boolean = true;

  public mounted(): void {
    this.getSites();
    this.getProjectSites(this.projectDetail.id);
  }

  public handleSelectedSiteArray(newValue: Site[]): void {
    this.renderComponent = false;
    this.localSelectedSiteArray = newValue;
    this.$nextTick(() => {
      this.renderComponent = true;
    });
  }

  public handleRemoveSelectedSite(index: number): void {
    const siteDetails: ProjectSite | undefined = (this.projectSites || []).find(
      (ps) => ps.site.id === this.localSelectedSiteArray[index].id,
    );
    if (siteDetails && siteDetails.auditCount && siteDetails.auditCount > 0) {
      Dialog.confirm({
        message: `<h1 class="title">Site can't be removed as it has been included in a Data Collection Period.</h1>`,
        confirmText: 'Ok',
        onConfirm: () => {},
      });
    } else {
      this.renderComponent = false;
      this.localSelectedSiteArray.splice(index, 1);
      this.$nextTick(() => {
        this.renderComponent = true;
      });
      this.localSelectedSiteArray = this.sanitizeSiteList(
        this.localSelectedSiteArray,
      );
    }
  }

  public sanitizeSiteList(arr: Site[]): Site[] {
    return arr.filter((site) => {
      return site.name.trim() ? site.name : false;
    });
  }

  public addSiteCard(): void {
    this.localSelectedSiteArray.push({
      id: 0,
      name: '',
      address: '',
      description: '',
      isIdentifierRequired: false,
    } as Site);
  }

  public transformSites(sites: ProjectSite[]): void {
    this.localSelectedSiteArray = [];
    sites.forEach((site: ProjectSite) => {
      const transformedSite: Site = {
        id: site.site.id,
        name: site.site.name,
        address: site.site.address,
        description: site.site.description,
        isIdentifierRequired: site.isIdentifierRequired,
      };
      this.localSelectedSiteArray.push(transformedSite);
    });
    this.localSelectedSiteArray = this.localSelectedSiteArray.sort(
      (site1, site2) => site1.id - site2.id,
    );
  }

  public updateSitesHandler(): void {
    this.updateProjectSites({
      projectId: this.projectDetail.id,
      sites: this.localSelectedSiteArray,
    });
  }

  @Watch('projectSites')
  @isTruthy
  public watchProjectSites(): void {
    if (this.projectSites !== undefined && this.projectSites.length > 0) {
      this.transformSites(this.projectSites || []);
    } else {
      this.addSiteCard();
    }
  }

  @Watch('updatedProjectSites')
  @isTruthy
  public updateSavedSites(): void {
    this.isSiteSubmitButtonDisabled = true;
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
    });
  }

  @Watch('localSelectedSiteArray', { deep: true })
  public onChangeLocalSelectedSiteArray(): void {
    this.isSiteSubmitButtonDisabled =
      JSON.parse(JSON.stringify(this.localSelectedSiteArray)) ===
      JSON.parse(JSON.stringify(this.projectSites));
  }
}
</script>
<style lang="scss" module>
.projectSettingsEditContainer {
  width: 680px;
  margin-right: auto;
  margin-left: 20px;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  padding: 0 calc(1em + 0.25em);
  font-weight: 700 !important;
  font-size: 18px !important;
  line-height: 22px !important;
}
.periodManagement {
  color: #919191;
  font-weight: 700;
}
.rightButtons {
  text-decoration: none !important;
  box-shadow: none !important;
  background: none !important;
}
.addItem {
  color: #21b5ea;
  font-weight: 400;
  margin-top: -11px;
}
.sitecolumns {
  margin-bottom: 0 !important;
}

.marginRight50 {
  margin-right: 50px !important;
}
</style>
